import React from 'react';
import { Menu } from 'semantic-ui-react';
import { Link, useLocation } from 'react-router-dom';
import history from 'helpers/history';
import { IUser, IPolicyScan } from 'types';
import styles from './styles/navbar.module.scss';
import classnames from 'classnames';
import {
  HOME_ROUTE,
  NOTIFICATIONS_ROUTE,
  DASHBOARD_ROUTE,
  EXPLORE_ROUTE,
  MARKET_SCAN_ROUTE,
} from 'constants/routes';
import Logo from 'components/logo';
import { NotificationPopup } from 'components/notification-list';
import { HelpTooltip } from 'components/tooltip';
import { getNavbarIcon, getNavbarTitle, getPageName } from './utils';
import {
  hasNavbarPageTitle,
  isMarketScanIndexPage,
  isMarketScanResultsPage,
  hasMarketScanExitBar,
  showMSStatusHeader,
  isMSResults,
} from 'containers/layout/utils';
import TestimonialAnimation from 'containers/market-scan/components/TestimonialAnimation';
import { MarketScanIconV2 } from 'components/svgs/MarketScanIconV2';
import { MarketScanInProgressIcon } from 'components/svgs/MarketScanInProgressIcon';
import { MBIIconV2 } from 'components/svgs/MBIIconV2';
import { HomeIcon } from 'components/svgs/HomeIcon';
import { DashboardIconNew } from 'components/svgs/DashboardIconNew';
import { ExploreIcon } from 'components/svgs/ExploreIcon';
import { Bell, ChevronLeft } from 'react-feather';
import { Button } from 'components/quashed';
import { isNewMarketScanLifePage } from 'containers/layout/utils';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import NavBarMarkScanOptions from './NavBarMarketScanOptions';
import { isThirdPartyMarketScan } from 'helpers';
import NavBarMarkScanOptionsMobile from './NavBarMarketScanOptionsMobile';
import MarketScanStepper from 'components/market-scan-stepper';

interface INavBarProps {
  openMobile?: boolean;
  user: IUser;
  navbarTitle?: string;
  policyScan?: IPolicyScan;
  /** Desktop: taller header that carries the page title underline. */
  hasPageTitle?: boolean;
  openMobileSidebar(): void;
}
interface INavTitleProps {
  className?: string;
  title?: string;
  navbarTitle?: string;
  policyScan?: IPolicyScan;
}
interface IMSHeaderProps {
  className?: string;
  policyScan: IPolicyScan;
  navbarTitle?: string;
}

const MarketScanWaitingPageTitle = ({
  policyScan,
  navbarTitle,
}: {
  navbarTitle?: string;
  policyScan?: IPolicyScan;
}) => {
  const isMbiScan = policyScan?.policyType === INSURANCE_TYPES_NAMES.MBI;
  const navbarIconSrc = getNavbarIcon(policyScan, navbarTitle);

  return (
    <>
      <div className={styles.msHeaderTitle}>
        {navbarIconSrc ? (
          <img className={styles.navbarIcon} src={navbarIconSrc} />
        ) : isMbiScan ? (
          <MBIIconV2 className={styles.navbarIcon} />
        ) : null}
        <p className={styles.navbarTitle}>
          {getNavbarTitle(policyScan, navbarTitle)}
        </p>

        {isMarketScanResultsPage(location.pathname) && (
          <HelpTooltip
            toggleContainerClassName={styles.tooltip}
            position="bottom center"
            className={styles.helpTooltip}
            basic={true}
            content={
              <>
                <b>Important:</b> Comparison table is for information purposes
                only. We try to avoid errors but they may exist. Some
                assumptions may have been necessary to provide you with these
                quotes. Providers and policies shown are not representative of
                the entire market nor are they an indication of any endorsement,
                affiliation or a commercial relationship with Quashed. If you
                use our service, click on a link, express interest in, or
                acquire a policy via Quashed, we may receive remuneration.
                Policies shown are not a recommendation nor are they an
                indication that a policy is the best in the market. You can read
                our FAQs, Privacy Policy and Terms & Conditions for more
                information.
              </>
            }
          />
        )}

        {isMarketScanResultsPage(location.pathname) && isMbiScan && (
          <div data-mbi-navbar-slot="" className={styles.mbiOptionsSlot} />
        )}
        {isMarketScanResultsPage(location.pathname) &&
          [
            INSURANCE_TYPES_NAMES.VEHICLE,
            INSURANCE_TYPES_NAMES.CONTENT,
            INSURANCE_TYPES_NAMES.HOME,
            INSURANCE_TYPES_NAMES.LANDLORD,
            INSURANCE_TYPES_NAMES.HOME_CONTENTS,
            INSURANCE_TYPES_NAMES.MOTORCYCLE,
            INSURANCE_TYPES_NAMES.RV,
          ].includes(policyScan?.policyType || '') &&
          policyScan && (
            <NavBarMarkScanOptions
              showCoverType={
                policyScan.policyType === INSURANCE_TYPES_NAMES.VEHICLE
              }
              policySubTypeName={policyScan.policySubTypeName}
              amount={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.amountHome
                  : policyScan.amount
              }
              excess={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.excessHome
                  : policyScan.excess
              }
              excessContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessContents
              }
              excessHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessHome
              }
              amountContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountContents
              }
              amountHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountHome
              }
              thirdPartyScan={isThirdPartyMarketScan(policyScan)}
            />
          )}
      </div>
      <div className={styles.msHeaderMobileOptions}>
        {isMarketScanResultsPage(location.pathname) &&
          [
            INSURANCE_TYPES_NAMES.VEHICLE,
            INSURANCE_TYPES_NAMES.CONTENT,
            INSURANCE_TYPES_NAMES.HOME,
            INSURANCE_TYPES_NAMES.LANDLORD,
            INSURANCE_TYPES_NAMES.HOME_CONTENTS,
            INSURANCE_TYPES_NAMES.MOTORCYCLE,
            INSURANCE_TYPES_NAMES.RV,
          ].includes(policyScan?.policyType || '') &&
          policyScan && (
            <NavBarMarkScanOptionsMobile
              showCoverType={
                policyScan.policyType === INSURANCE_TYPES_NAMES.VEHICLE
              }
              policySubTypeName={policyScan.policySubTypeName}
              amount={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.amountHome
                  : policyScan.amount
              }
              excess={
                policyScan.policyType === INSURANCE_TYPES_NAMES.LANDLORD
                  ? policyScan.excessHome
                  : policyScan.excess
              }
              excessContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessContents
              }
              excessHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.excessHome
              }
              amountContents={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountContents
              }
              amountHome={
                policyScan.policyType === INSURANCE_TYPES_NAMES.HOME_CONTENTS &&
                policyScan.amountHome
              }
              thirdPartyScan={isThirdPartyMarketScan(policyScan)}
            />
          )}
      </div>
    </>
  );
};

export const MarketScanWaitingPageHeader = ({
  className,
  policyScan,
  navbarTitle,
}: IMSHeaderProps) => {
  return (
    <div className={styles.msHeaderContainer}>
      <TestimonialAnimation />
      <MarketScanWaitingPageTitle
        policyScan={policyScan}
        navbarTitle={navbarTitle}
      />
    </div>
  );
};

export const MarketScanResultsTitle = ({
  policyScan,
  navbarTitle,
}: IMSHeaderProps) => (
  <div className={styles.msResultsTitle}>
    <MarketScanWaitingPageTitle
      policyScan={policyScan}
      navbarTitle={navbarTitle}
    />
  </div>
);

export const NavTitle = ({
  className,
  title,
  navbarTitle,
  policyScan,
}: INavTitleProps) => {
  const location = useLocation();
  let name = title || getPageName(location);
  let showUnderline = true;
  let wrapperClass = '';

  const isHomePage = location.pathname === HOME_ROUTE;
  const isDashboardPage = location.pathname === DASHBOARD_ROUTE;
  const isMarketScanPage = isMarketScanIndexPage(location.pathname);
  const isExplorePage = location.pathname.includes(EXPLORE_ROUTE);
  const isNotificationsPage = location.pathname === NOTIFICATIONS_ROUTE;
  const pageTitleWrapperClass = classnames(
    styles.pageTitleWrapper,
    hasNavbarPageTitle(location.pathname) && styles.pageTitleWrapperWithText,
  );

  if (isHomePage) {
    name = (
      <div className={styles.pageTitle}>
        <HomeIcon className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Home</span>
      </div>
    );
    wrapperClass = pageTitleWrapperClass;
  }

  if (isDashboardPage) {
    name = (
      <div className={styles.pageTitle}>
        <DashboardIconNew className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>My Dashboard</span>
      </div>
    );
    wrapperClass = pageTitleWrapperClass;
  }

  if (isMarketScanPage) {
    name = (
      <div className={styles.pageTitle}>
        <MarketScanIconV2 className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Market Scan</span>
      </div>
    );
    wrapperClass = pageTitleWrapperClass;
  }

  if (isExplorePage) {
    name = (
      <div className={styles.pageTitle}>
        <ExploreIcon className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Explore Insurance</span>
      </div>
    );
    wrapperClass = pageTitleWrapperClass;
  }

  if (isNotificationsPage) {
    name = (
      <div className={styles.pageTitle}>
        <Bell className={styles.pageTitleIcon} />
        <span className={styles.pageTitleText}>Notifications</span>
      </div>
    );
    wrapperClass = pageTitleWrapperClass;
  }

  if (isNewMarketScanLifePage(location.pathname)) {
    name = (
      <div className={styles.marketScanTitle}>
        {navbarTitle ? <MarketScanInProgressIcon /> : <MarketScanIconV2 />}
        {navbarTitle || 'Market Scan'}
      </div>
    );
    showUnderline = false;
  }

  if (isMSResults(location.pathname)) {
    name = (
      <MarketScanWaitingPageTitle
        policyScan={policyScan}
        navbarTitle={navbarTitle}
      />
    );
    showUnderline = false;
  }

  return (
    <div
      className={classnames(
        styles.title,
        className,
        wrapperClass,
        isMSResults(location.pathname) && styles.noPadding,
      )}
    >
      <span>{name}</span>
      {showUnderline ? <div className={styles.bar} /> : null}
    </div>
  );
};

const NavBar = ({
  openMobile,

  openMobileSidebar,
  navbarTitle,
  policyScan,
  hasPageTitle,
}: INavBarProps) => {
  const showExitBar = hasMarketScanExitBar(location.pathname, policyScan);

  return (
    <Menu
      className={classnames(
        styles.navbar,
        hasPageTitle && styles.navbarWithPageTitle,
        showExitBar && styles.navbarMsExit,
      )}
      pointing={true}
      secondary={true}
    >
      <Menu.Item className={styles.hamburger} position="left">
        <div
          className={classnames(styles.burger, openMobile && styles.active)}
          onClick={openMobileSidebar}
          data-ignore-outside-clicks={true}
        />
      </Menu.Item>
      <Menu.Item as={Link} header={true} className={styles.brand} to="/">
        <Logo />
      </Menu.Item>
      <Menu.Item className={styles.hamburger} position="right">
        <NotificationPopup
          className={styles.mobileNotif}
          onOpen={() => history.push(NOTIFICATIONS_ROUTE)}
          isMobile={true}
        />
      </Menu.Item>
      {showExitBar ? (
        <>
          <Menu.Item className={styles.msExit}>
            <Button
              className={styles.msExitButton}
              type="button"
              onClick={() => history.push(MARKET_SCAN_ROUTE)}
            >
              <ChevronLeft size={16} />
              Back to Market Scan
            </Button>
          </Menu.Item>
          <Menu.Item className={styles.msStepper}>
            <MarketScanStepper key={policyScan?.id} policyScan={policyScan} />
          </Menu.Item>
        </>
      ) : !showMSStatusHeader(location.pathname) ? (
        <NavTitle policyScan={policyScan} navbarTitle={navbarTitle} />
      ) : (
        <MarketScanWaitingPageTitle
          policyScan={policyScan}
          navbarTitle={navbarTitle}
        />
      )}
      <Menu.Item
        className={classnames(
          styles.right,
          showMSStatusHeader(location.pathname) && styles.msWaitingNotification,
        )}
        position="right"
      >
        <NotificationPopup
          popupClassName={styles.notification}
          isMobile={false}
        />
      </Menu.Item>
    </Menu>
  );
};

export default NavBar;
